import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiPlus, FiSearch, FiUserX, FiMail, FiPhone } from 'react-icons/fi';
import { AdminLayout } from '../../components/layout';
import { Card, Button, Loading } from '../../components/common';
import { getAllManagers, removeManager } from '../../firebase/users';
import { getAllCompanies } from '../../firebase/companies';

const ManagerList = () => {
  const [managers, setManagers] = useState([]);
  const [companies, setCompanies] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [managersResult, companiesResult] = await Promise.all([
        getAllManagers(),
        getAllCompanies()
      ]);

      setManagers(managersResult.managers || []);
      
      // Create company lookup map
      const companyMap = {};
      (companiesResult.companies || []).forEach(company => {
        companyMap[company.id] = company;
      });
      setCompanies(companyMap);

    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveManager = async (manager) => {
    if (!window.confirm(`Are you sure you want to remove manager access for "${manager.displayName || manager.email}"?`)) {
      return;
    }

    const result = await removeManager(manager.id);
    
    if (!result.error) {
      setManagers(managers.filter(m => m.id !== manager.id));
    }
  };

  // Filter managers
  const filteredManagers = managers.filter(manager => {
    const searchLower = searchQuery.toLowerCase();
    return (
      manager.displayName?.toLowerCase().includes(searchLower) ||
      manager.email?.toLowerCase().includes(searchLower) ||
      companies[manager.companyId]?.name?.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <AdminLayout title="Managers">
        <div className="flex items-center justify-center h-64">
          <Loading />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Managers">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">All Managers</h2>
          <p className="text-gray-600">{managers.length} total managers</p>
        </div>
        <Link to="/admin/managers/new">
          <Button className="flex items-center gap-2">
            <FiPlus size={18} />
            Add Manager
          </Button>
        </Link>
      </div>

      {/* Search */}
      <Card className="p-4 mb-6">
        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search by name, email, or company..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
      </Card>

      {/* Managers List */}
      {filteredManagers.length > 0 ? (
        <div className="space-y-4">
          {filteredManagers.map((manager) => {
            const company = companies[manager.companyId];
            
            return (
              <Card key={manager.id} className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                      {manager.photoURL ? (
                        <img 
                          src={manager.photoURL} 
                          alt={manager.displayName}
                          className="w-full h-full rounded-full object-cover"
                        />
                      ) : (
                        <span className="text-xl font-semibold text-primary-600">
                          {(manager.displayName || manager.email)?.[0]?.toUpperCase()}
                        </span>
                      )}
                    </div>
                    
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {manager.displayName || 'No Name'}
                      </h3>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <FiMail size={14} />
                          {manager.email}
                        </span>
                        {manager.phone && (
                          <span className="flex items-center gap-1">
                            <FiPhone size={14} />
                            {manager.phone}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    {company ? (
                      <Link 
                        to={`/admin/companies/${company.id}`}
                        className="px-3 py-1 bg-gray-100 rounded-full text-sm text-gray-700 hover:bg-gray-200"
                      >
                        {company.name}
                      </Link>
                    ) : (
                      <span className="px-3 py-1 bg-yellow-100 rounded-full text-sm text-yellow-700">
                        No Company Assigned
                      </span>
                    )}
                    
                    <button
                      onClick={() => handleRemoveManager(manager)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      title="Remove manager access"
                    >
                      <FiUserX size={18} />
                    </button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="p-12 text-center">
          <div className="text-gray-400 mb-4">
            <FiSearch size={48} className="mx-auto" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {searchQuery ? 'No managers found' : 'No managers yet'}
          </h3>
          <p className="text-gray-600 mb-4">
            {searchQuery 
              ? 'Try adjusting your search'
              : 'Start by assigning managers to companies'
            }
          </p>
          {!searchQuery && (
            <Link to="/admin/managers/new">
              <Button>Add First Manager</Button>
            </Link>
          )}
        </Card>
      )}
    </AdminLayout>
  );
};

export default ManagerList;
